import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { toast } from '@/hooks/use-toast';

export type TransactionType = 'income' | 'fixed_expense' | 'variable_expense' | 'saving' | 'investment' | 'debt_payment';
export type PaymentMethod = 'cash' | 'debit_card' | 'credit_card' | 'transfer' | 'check' | 'other';
export type Currency = 'USD' | 'CRC';

export interface Transaction {
  id: string;
  user_id: string;
  date: string;
  type: TransactionType;
  amount: number;
  category: string;
  payment_method: PaymentMethod;
  currency: Currency;
  notes: string | null;
  debt_id: string | null;
  created_at: string;
  original_amount?: number | null;
  original_currency?: Currency | null;
  exchange_rate_used?: number | null;
  was_converted?: boolean;
}

export interface CreateTransactionInput {
  date: string;
  type: TransactionType;
  amount: number;
  category: string;
  payment_method: PaymentMethod;
  currency: Currency;
  notes?: string;
  debt_id?: string | null;
  original_amount?: number | null;
  original_currency?: Currency | null;
  exchange_rate_used?: number | null;
  was_converted?: boolean;
}

export function useTransactions(year?: number, month?: number) {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const { data: transactions = [], isLoading, error } = useQuery({
    queryKey: ['transactions', user?.id, year, month],
    queryFn: async () => {
      if (!user) return [];
      
      let query = supabase
        .from('transactions')
        .select('*')
        .eq('user_id', user.id)
        .order('date', { ascending: false });

      if (year && month) {
        const startDate = `${year}-${String(month).padStart(2, '0')}-01`;
        const endDate = new Date(year, month, 0).toISOString().split('T')[0];
        query = query.gte('date', startDate).lte('date', endDate);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data as Transaction[];
    },
    enabled: !!user,
  });

  const createTransaction = useMutation({
    mutationFn: async (input: CreateTransactionInput) => {
      if (!user) throw new Error('No user');
      
      const { data, error } = await supabase
        .from('transactions')
        .insert({
          user_id: user.id,
          date: input.date,
          type: input.type,
          amount: input.amount,
          category: input.category,
          payment_method: input.payment_method as 'cash' | 'debit_card' | 'credit_card' | 'transfer' | 'other',
          currency: input.currency,
          notes: input.notes,
          debt_id: input.debt_id || null,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['transactions'] });
      queryClient.invalidateQueries({ queryKey: ['monthly-summary'] });
      queryClient.invalidateQueries({ queryKey: ['debts'] });
      toast({ title: 'Transacción creada', description: 'La transacción se ha registrado correctamente.' });
    },
    onError: (error) => {
      toast({ title: 'Error', description: 'No se pudo crear la transacción.', variant: 'destructive' });
    },
  });

  const updateTransaction = useMutation({
    mutationFn: async ({ id, ...input }: Partial<CreateTransactionInput> & { id: string }) => {
      const updateData: Record<string, unknown> = {};
      if (input.date !== undefined) updateData.date = input.date;
      if (input.type !== undefined) updateData.type = input.type;
      if (input.amount !== undefined) updateData.amount = input.amount;
      if (input.category !== undefined) updateData.category = input.category;
      if (input.payment_method !== undefined) updateData.payment_method = input.payment_method;
      if (input.currency !== undefined) updateData.currency = input.currency;
      if (input.notes !== undefined) updateData.notes = input.notes;
      if (input.debt_id !== undefined) updateData.debt_id = input.debt_id;
      if (input.original_amount !== undefined) updateData.original_amount = input.original_amount;
      if (input.original_currency !== undefined) updateData.original_currency = input.original_currency;
      if (input.exchange_rate_used !== undefined) updateData.exchange_rate_used = input.exchange_rate_used;
      if (input.was_converted !== undefined) updateData.was_converted = input.was_converted;

      const { data, error } = await supabase
        .from('transactions')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['transactions'] });
      queryClient.invalidateQueries({ queryKey: ['monthly-summary'] });
      queryClient.invalidateQueries({ queryKey: ['debts'] });
      toast({ title: 'Transacción actualizada' });
    },
    onError: () => {
      toast({ title: 'Error', description: 'No se pudo actualizar la transacción.', variant: 'destructive' });
    },
  });

  const deleteTransaction = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('transactions')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['transactions'] });
      queryClient.invalidateQueries({ queryKey: ['monthly-summary'] });
      queryClient.invalidateQueries({ queryKey: ['debts'] });
      toast({ title: 'Transacción eliminada' });
    },
    onError: () => {
      toast({ title: 'Error', description: 'No se pudo eliminar la transacción.', variant: 'destructive' });
    },
  });

  const bulkCreateTransactions = useMutation({
    mutationFn: async (inputs: CreateTransactionInput[]) => {
      if (!user) throw new Error('No user');
      
      const transactionsToInsert = inputs.map(input => ({
        user_id: user.id,
        date: input.date,
        type: input.type,
        amount: input.amount,
        category: input.category,
        payment_method: input.payment_method as 'cash' | 'debit_card' | 'credit_card' | 'transfer' | 'other',
        currency: input.currency,
        notes: input.notes || null,
        debt_id: input.debt_id || null,
        original_amount: input.original_amount ?? null,
        original_currency: input.original_currency ?? null,
        exchange_rate_used: input.exchange_rate_used ?? null,
        was_converted: input.was_converted ?? false,
      }));

      const { data, error } = await supabase
        .from('transactions')
        .insert(transactionsToInsert)
        .select();

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['transactions'] });
      queryClient.invalidateQueries({ queryKey: ['monthly-summary'] });
      queryClient.invalidateQueries({ queryKey: ['debts'] });
    },
    onError: (error) => {
      console.error('Bulk import error:', error);
      toast({ title: 'Error', description: 'No se pudieron importar las transacciones.', variant: 'destructive' });
    },
  });

  const bulkUpdateCategory = useMutation({
    mutationFn: async ({ ids, category }: { ids: string[]; category: string }) => {
      if (!user) throw new Error('No user');
      
      const { data, error } = await supabase
        .from('transactions')
        .update({ category })
        .in('id', ids)
        .select();

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['transactions'] });
      queryClient.invalidateQueries({ queryKey: ['monthly-summary'] });
      toast({ title: 'Categorías actualizadas', description: `Se actualizaron ${data.length} transacciones.` });
    },
    onError: () => {
      toast({ title: 'Error', description: 'No se pudieron actualizar las transacciones.', variant: 'destructive' });
    },
  });

  const bulkDeleteTransactions = useMutation({
    mutationFn: async (ids: string[]) => {
      if (!user) throw new Error('No user');
      
      const { error } = await supabase
        .from('transactions')
        .delete()
        .in('id', ids);

      if (error) throw error;
    },
    onSuccess: (_data, ids) => {
      queryClient.invalidateQueries({ queryKey: ['transactions'] });
      queryClient.invalidateQueries({ queryKey: ['monthly-summary'] });
      queryClient.invalidateQueries({ queryKey: ['debts'] });
      toast({ title: 'Transacciones eliminadas', description: `Se eliminaron ${ids.length} transacciones.` });
    },
    onError: () => {
      toast({ title: 'Error', description: 'No se pudieron eliminar las transacciones.', variant: 'destructive' });
    },
  });

  return {
    transactions,
    isLoading,
    error,
    createTransaction,
    updateTransaction,
    deleteTransaction,
    bulkCreateTransactions,
    bulkUpdateCategory,
    bulkDeleteTransactions,
  };
}
